import type {
  EventIdPublic,
  EventParticipantIdPublic,
} from "../schemas/branded-id.schemas.js";
// `TriggerPipelineVariables` (below) is FE-mutation transport that carries an
// `Experiment` payload; type-only, and experiment.schema imports only zod, so
// this reverses the usual data-objects→types edge without a runtime cycle.
import type { Experiment } from "../schemas/experiment.schemas.js";

/**
 * Topbar / detail-pane selection key. Two fixed leading slots (`input`,
 * `enrichment`) plus any step-result type that appears on the snapshot —
 * the rest of the topbar pills are derived dynamically. `StepResultSelection`
 * is just `string` so new engine step types flow through without any
 * front-end taxonomy edits.
 */
export const PIPELINE_FIXED_SELECTIONS = ["input", "enrichment"] as const;
export type PipelineFixedSelection = (typeof PIPELINE_FIXED_SELECTIONS)[number];

export type StepResultSelection = string;

// `(string & {})` preserves IDE autocomplete for `PipelineFixedSelection`
// while still accepting any step-type string at the type level.
export type PipelineSelection = PipelineFixedSelection | (string & {});

export const STAGE_STATUSES = ["done", "failed", "missing"] as const;
export type StageStatus = (typeof STAGE_STATUSES)[number];

/**
 * Path token standing in for "the run the engine last started" where a run id is
 * expected, so the snapshot has one URL shape whether or not the caller pinned a
 * run. Same convention as `events/:id/participants/me`.
 */
export const CURRENT_RUN_PATH_TOKEN = "current";

export const MATCHING_RUN_STATUSES = [
  "PENDING",
  "RUNNING",
  "COMPLETED",
  "FAILED",
  "CANCELLED",
] as const;
// Ordered top-level phases of a pipeline run. The array order IS the execution
// order — the orchestrator's continue-logic, target-stage cutoff, and the admin
// progress stepper all read it here so the sequence lives in one place.
export const MATCHING_PIPELINE_PHASES = ["learn", "connect", "render"] as const;
export type MatchingPipelinePhase = (typeof MATCHING_PIPELINE_PHASES)[number];

export const MatchingEngineRunMode = {
  INCREMENTAL: "incremental",
  RECOMPUTE: "recompute",
} as const;

export type MatchingEngineRunModeType =
  (typeof MatchingEngineRunMode)[keyof typeof MatchingEngineRunMode];

export interface TriggerPipelineVariables {
  readonly eventIdPublic: EventIdPublic;
  readonly mode?: MatchingEngineRunModeType;
  readonly targetStage?: MatchingPipelinePhase;
  readonly experiment?: Experiment;
  // Scopes connect and render to these participants; omitted covers the whole
  // roster. Learn runs for everyone either way.
  readonly participantIdsPublic?: readonly EventParticipantIdPublic[];
}

export type TriggerPipelineResult = Record<string, never>;

export interface CancelRunVariables {
  // Plain string: the run's public id comes off the run snapshot as a string.
  readonly runIdPublic: string;
  // Scopes the request — cancel lives under the event and refuses a run that
  // belongs to another one — and invalidates that event's queries on success.
  readonly eventIdPublic: EventIdPublic;
}

export interface CancelRunResult {
  readonly cancelled: boolean;
}
